// src/features/players/usePlayer.ts

import { useQuery } from '@tanstack/react-query';
import { PlayerApi } from './PlayerApi';
import { PLAYER_QUERY_KEYS } from '@/lib/constants/player';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { PlayerViewData } from '@/lib/types/Player';

export interface UsePlayerResult {
  player: PlayerViewData | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch a single player by numeric ID.
 * Uses the detail query key for granular cache control.
 */
export function usePlayer(playerId: number | null): UsePlayerResult {
  const { selectedCId } = useAuth();

  const query = useQuery({
    queryKey: PLAYER_QUERY_KEYS.detail(selectedCId ?? 0, playerId ?? 0),
    queryFn: async () => PlayerApi.getPlayer(selectedCId!, playerId!),
    enabled: selectedCId != null && playerId != null,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    player: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
